import React, { useEffect, useRef, useState } from "react"
import { ArrowLeft } from "lucide-react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

type TableKey = 'fm' | 'fr' | 'afm' | 'ar'

interface FormData059_1 {
  ROW_ID?: string
  numero_solicitud: string
  fecha: string
  hora: string
  temperatura: string
  hr: string
  id_termohigrometro: string
  longitud_ensayo: string
  velocidad_alargamiento: string
  ancho_tira: string
  estado_especimenes: string[]
  num_especimenes: string
  especimenes_rechazados: string
  num_hilos_tira: string
  tablas_activas: string[]
  reportar: boolean
  tiene_metodo_muestreo: boolean
  metodo_muestreo: string
  fm_u1: string; fm_u2: string; fm_u3: string; fm_u4: string; fm_u5: string
  fm_t1: string; fm_t2: string; fm_t3: string; fm_t4: string; fm_t5: string
  fm_prom_u: string; fm_prom_t: string; fm_red_u: string; fm_red_t: string
  fm_txt_u: string; fm_txt_t: string
  fr_u1: string; fr_u2: string; fr_u3: string; fr_u4: string; fr_u5: string
  fr_t1: string; fr_t2: string; fr_t3: string; fr_t4: string; fr_t5: string
  fr_prom_u: string; fr_prom_t: string; fr_red_u: string; fr_red_t: string
  fr_txt_u: string; fr_txt_t: string
  afm_u1: string; afm_u2: string; afm_u3: string; afm_u4: string; afm_u5: string
  afm_t1: string; afm_t2: string; afm_t3: string; afm_t4: string; afm_t5: string
  afm_prom_u: string; afm_prom_t: string; afm_red_u: string; afm_red_t: string
  afm_txt_u: string; afm_txt_t: string
  ar_u1: string; ar_u2: string; ar_u3: string; ar_u4: string; ar_u5: string
  ar_t1: string; ar_t2: string; ar_t3: string; ar_t4: string; ar_t5: string
  ar_prom_u: string; ar_prom_t: string; ar_red_u: string; ar_red_t: string
  ar_txt_u: string; ar_txt_t: string
  observaciones: string
  mostrarObservaciones: boolean
  desv_metodo: string
  realizo: string
  superviso: string
}

interface Props {
  mode: "create" | "view" | "edit"
  initialData?: any
  onSave?: (data: any) => void
  onCancel?: () => void
  realizoUser?: string
  onRefreshBackend?: () => void
  rol?: string
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

function roundGrab(val: number): number {
  if (val < 100) return Math.round(val)
  if (val < 1000) return Math.round(val / 10) * 10
  return Math.round(val / 100) * 100
}

function roundAlarg(val: number): string {
  if (val <= 7) return (Math.round(val / 0.2) * 0.2).toFixed(1)
  if (val <= 75) return (Math.round(val / 0.5) * 0.5).toFixed(1)
  return String(Math.round(val))
}

function calcProm(vals: string[]): string {
  const nums = vals.map(v => parseFloat(v)).filter(n => !isNaN(n))
  if (nums.length === 0) return ''
  return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(3)
}

function calcDesv1(vals: string[]): string {
  const nums = vals.map(v => parseFloat(v)).filter(n => !isNaN(n))
  if (nums.length < 2) return ''
  const mean = nums.reduce((a, b) => a + b, 0) / nums.length
  return Math.sqrt(nums.reduce((s, x) => s + Math.pow(x - mean, 2), 0) / (nums.length - 1)).toFixed(3)
}

function calcCV1(prom: string, desv: string): string {
  const p = parseFloat(prom); const d = parseFloat(desv)
  if (!p || !d || isNaN(p) || isNaN(d)) return ''
  return ((d / p) * 100).toFixed(2)
}

const T_TABLE_1: Record<number, number> = { 2: 12.706, 3: 4.303, 4: 3.182, 5: 2.776, 6: 2.571, 7: 2.447, 8: 2.365, 9: 2.306, 10: 2.262 }

function calcLC95_1(desv: string, vals: string[]): string {
  const d = parseFloat(desv)
  const n = vals.filter(v => v !== '' && !isNaN(parseFloat(v))).length
  if (!d || isNaN(d) || n < 2) return ''
  return ((T_TABLE_1[n] ?? 2.776) * d / Math.sqrt(n)).toFixed(3)
}

const makeDefaults = (): FormData059_1 => {
  const d: any = {
    numero_solicitud: '', fecha: '', hora: '', temperatura: '', hr: '',
    id_termohigrometro: '', longitud_ensayo: '', velocidad_alargamiento: '',
    ancho_tira: '', estado_especimenes: [], num_especimenes: '',
    especimenes_rechazados: '', num_hilos_tira: '',
    tablas_activas: ['fm', 'fr', 'afm', 'ar'],
    reportar: true,
    tiene_metodo_muestreo: false,
    metodo_muestreo: '',
    observaciones: '', mostrarObservaciones: false, desv_metodo: '', realizo: '', superviso: '',
  }
  for (const t of ['fm', 'fr', 'afm', 'ar']) {
    for (let i = 1; i <= 5; i++) { d[`${t}_u${i}`] = ''; d[`${t}_t${i}`] = '' }
    d[`${t}_prom_u`] = ''; d[`${t}_prom_t`] = ''
    d[`${t}_red_u`] = ''; d[`${t}_red_t`] = ''
    d[`${t}_txt_u`] = ''; d[`${t}_txt_t`] = ''
  }
  return d
}

const TABLE_LABELS: Record<TableKey, { label: string; unit: string; isAlarg: boolean }> = {
  fm:  { label: 'FUERZA MÁXIMA',                   unit: 'N', isAlarg: false },
  fr:  { label: 'FUERZA A LA ROTURA',               unit: 'N', isAlarg: false },
  afm: { label: 'ALARGAMIENTO A LA FUERZA MÁXIMA',  unit: '%', isAlarg: true  },
  ar:  { label: 'ALARGAMIENTO A LA ROTURA',          unit: '%', isAlarg: true  },
}

const ESTADO_OPTIONS = ['Acondicionados', 'Húmedos', 'Con tensión previa', 'Relajados']
const ALL_TABLES: TableKey[] = ['fm', 'fr', 'afm', 'ar']

// Shared cell style
const TD = "border border-gray-500 px-1 py-0.5 align-top text-xs"

export function NMXA059_1Form({ mode, initialData = {}, onSave, onCancel, realizoUser, onRefreshBackend, rol, pedidoRowId, onAutosave }: Props) {
  const globalRol = useUserRol()
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol

  const parseArr = (raw: any): string[] => {
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string' && raw) {
      try { return JSON.parse(raw) } catch { return raw.split(',').map((s: string) => s.trim()).filter(Boolean) }
    }
    return []
  }

  const [formData, setFormData] = useState<FormData059_1>({
    ...makeDefaults(),
    realizo: mode === 'create' && realizoUser ? realizoUser : '',
    ...initialData,
    estado_especimenes: parseArr(initialData?.estado_especimenes),
    tablas_activas: parseArr(initialData?.tablas_activas).length > 0
      ? parseArr(initialData?.tablas_activas) : ['fm', 'fr', 'afm', 'ar'],
    mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
  })
  const [editMode, setEditMode] = useState(false)
  const [showRechazoModal, setShowRechazoModal] = useState(false)
  const [rechazoLoading, setRechazoLoading] = useState(false)
  const [estadoOpen, setEstadoOpen] = useState(false)

  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    const ia = parseArr(initialData?.tablas_activas)
    setFormData({
      ...makeDefaults(),
      realizo: realizoUser || '',
      ...initialData,
      estado_especimenes: parseArr(initialData?.estado_especimenes),
      tablas_activas: ia.length > 0 ? ia : ['fm', 'fr', 'afm', 'ar'],
      mostrarObservaciones: initialData?.MOSTRAR_OBSERVACIONES === true,
    })
    setEditMode(false)
  }, [initialData])

  const aceptadoRaw = initialData?.ACEPTADO ?? initialData?.aceptado
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : ''
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado'
  const isReadOnly = mode === 'view' && !editMode
  const [autosaveRowId, setAutosaveRowId] = useState<string>(formData?.ROW_ID || initialData?.ROW_ID || '')
  const [autosaveNomId, setAutosaveNomId] = useState<string>(initialData?.nomId || '')
  const rowId = autosaveRowId
  const nomId = autosaveNomId

  const set = (field: keyof FormData059_1, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      for (const t of ALL_TABLES) {
        const uVals = [1,2,3,4,5].map(i => (next as any)[`${t}_u${i}`])
        const tVals = [1,2,3,4,5].map(i => (next as any)[`${t}_t${i}`])
        const promU = calcProm(uVals)
        const promT = calcProm(tVals)
        ;(next as any)[`${t}_prom_u`] = promU
        ;(next as any)[`${t}_prom_t`] = promT
        const { isAlarg } = TABLE_LABELS[t]
        if (promU) {
          const r = isAlarg ? roundAlarg(parseFloat(promU)) : String(roundGrab(parseFloat(promU)))
          ;(next as any)[`${t}_red_u`] = r
        }
        if (promT) {
          const r = isAlarg ? roundAlarg(parseFloat(promT)) : String(roundGrab(parseFloat(promT)))
          ;(next as any)[`${t}_red_t`] = r
        }
      }
      return next
    })
  }

  const toggleEstado = (est: string) => {
    if (isReadOnly) return
    setFormData(prev => {
      const arr = prev.estado_especimenes
      return { ...prev, estado_especimenes: arr.includes(est) ? arr.filter(e => e !== est) : [...arr, est] }
    })
  }

  // Close estado dropdown on outside click
  useEffect(() => {
    if (!estadoOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-estado-dropdown]')) setEstadoOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [estadoOpen])

  const toggleTabla = (t: TableKey) => {
    setFormData(prev => {
      const arr = prev.tablas_activas
      return { ...prev, tablas_activas: arr.includes(t) ? arr.filter(x => x !== t) : [...arr, t] }
    })
  }

  const formPayload = () => {
    const { ROW_ID, ...rest } = formData
    return {
      ...rest,
      estado_especimenes: formData.estado_especimenes.join(', '),
      tablas_activas: formData.tablas_activas.join(', '),
    }
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) onSave(formPayload())
    } finally {
      setGuardando(false)
    }
  }

  const handleGuardarEdicion = () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de NMX-A-059-1? Esta acción no se puede deshacer.')) return
    if (onSave) onSave({
      ...formPayload(),
      ROW_ID: rowId,
      _EDIT_MODE: true,
    } as any)
  }

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-059/1-INNTEX-2019 DETERMINACIÓN DE LA FUERZA MÁXIMA Y DEL ALARGAMIENTO A LA FUERZA MÁXIMA POR EL MÉTODO DE LA TIRA',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const handleAccion = async () => {
    if (!window.confirm('Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba NMX-A-059-1?')) return
    try {
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, 'aceptado', undefined, undefined, nomId || undefined)
      toast.success('Prueba aceptada correctamente.')
      onRefreshBackend?.()
    } catch (e: any) { toast.error(e.message) }
  }

  const inputCls = (extra?: string) =>
    `h-6 text-xs px-1 ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''} ${extra ?? ''}`

  return (
    <>
      {onCancel && (
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
      )}
      <Card className="w-full max-w-5xl mx-auto">
      <CardContent className="p-4 space-y-3">

        {/* ── HEADER TABLE ── */}
        <table className="w-full text-xs border-collapse border-2 border-gray-700">
          <tbody>

            {/* Row 1: Número de solicitud */}
            <tr>
              <td colSpan={5} className={TD}>
                <div className="flex items-center gap-2">
                  <span className="font-bold whitespace-nowrap">NÚMERO DE SOLICITUD:</span>
                  <Input
                    className={inputCls('w-48')}
                    value={formData.numero_solicitud}
                    readOnly={isReadOnly}
                    onChange={e => set('numero_solicitud', e.target.value)}
                  />
                </div>
              </td>
            </tr>

            {/* Row 2: Title + MET */}
            <tr>
              <td colSpan={4} className={`${TD} text-center font-bold leading-snug py-1`}>
                NMX-A-059/1-INNTEX-2019 DETERMINACIÓN DE LA FUERZA MÁXIMA Y DEL ALARGAMIENTO A LA FUERZA MÁXIMA POR EL MÉTODO DE LA TIRA
              </td>
              <td className={`${TD} text-center font-bold whitespace-nowrap`}>
                MET-7.2-24
              </td>
            </tr>

            {/* Row 3: Fecha / Hora / Temp / HR / ID Termohig */}
            <tr>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Fecha:</div>
                <Input type="date" className={inputCls('w-full')}
                  value={formData.fecha} readOnly={isReadOnly}
                  onChange={e => set('fecha', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Hora:</div>
                <Input type="time" className={inputCls('w-full')}
                  value={formData.hora} readOnly={isReadOnly}
                  onChange={e => { if (e.target.value) set('hora', e.target.value) }} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Temperatura(°C)</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.temperatura} readOnly={isReadOnly}
                  onChange={e => set('temperatura', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Humedad relativa (%)</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.hr} readOnly={isReadOnly}
                  onChange={e => set('hr', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">ID de termohigrómetro:</div>
                {isReadOnly ? (
                  <Input value={formData.id_termohigrometro} readOnly className={inputCls('w-full')} />
                ) : (
                  <Select value={formData.id_termohigrometro} onValueChange={v => set('id_termohigrometro', v)}>
                    <SelectTrigger className="h-6 text-xs"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="E-59">E-59</SelectItem>
                      <SelectItem value="E-57">E-57</SelectItem>
                      <SelectItem value="E-56">E-56</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </td>
            </tr>

            {/* Row 4: Longitud / Velocidad / Ancho */}
            <tr>
              <td colSpan={2} className={TD}>
                <div className="font-medium whitespace-nowrap">Longitud del ensayo (mm):</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.longitud_ensayo} readOnly={isReadOnly}
                  onChange={e => set('longitud_ensayo', e.target.value)} />
              </td>
              <td colSpan={2} className={TD}>
                <div className="font-medium whitespace-nowrap">Velocidad de alargamiento (mm/min):</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.velocidad_alargamiento} readOnly={isReadOnly}
                  onChange={e => set('velocidad_alargamiento', e.target.value)} />
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Ancho de la tira(mm):</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.ancho_tira} readOnly={isReadOnly}
                  onChange={e => set('ancho_tira', e.target.value)} />
              </td>
            </tr>

            {/* Row 5: Estado de especímenes — multi-select dropdown en una sola fila */}
            <tr>
              <td className={`${TD} whitespace-nowrap`}>
                <div className="font-medium">Estado de los</div>
                <div className="font-medium">especímenes:</div>
              </td>
              <td className={TD}>
                {/* Multi-select dropdown */}
                <div className="relative" data-estado-dropdown>
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => setEstadoOpen(prev => !prev)}
                    className="w-full h-7 text-xs border border-gray-300 rounded px-2 text-left flex items-center justify-between bg-white disabled:bg-transparent disabled:border-0 disabled:shadow-none disabled:cursor-not-allowed"
                  >
                    <span className="truncate text-gray-700">
                      {formData.estado_especimenes.length > 0
                        ? formData.estado_especimenes.join(', ')
                        : <span className="text-gray-400">Seleccionar...</span>}
                    </span>
                    {!isReadOnly && <span className="ml-1 text-gray-400">▾</span>}
                  </button>
                  {estadoOpen && !isReadOnly && (
                    <div className="absolute z-50 left-0 mt-1 w-48 bg-white border border-gray-300 rounded shadow-lg">
                      {ESTADO_OPTIONS.map(est => (
                        <label key={est} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                          <input type="checkbox"
                            checked={formData.estado_especimenes.includes(est)}
                            onChange={() => toggleEstado(est)} />
                          {est}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              <td className={TD}>
                <div className="font-medium whitespace-nowrap">Núm. especímenes:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.num_especimenes} readOnly={isReadOnly}
                  onChange={e => set('num_especimenes', e.target.value)} />
                <div className="font-medium whitespace-nowrap mt-1">Rechazados:</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.especimenes_rechazados} readOnly={isReadOnly}
                  onChange={e => set('especimenes_rechazados', e.target.value)} />
                <div className="flex items-center gap-1.5 mt-1">
                  <input type="checkbox" disabled={isReadOnly}
                    checked={formData.reportar !== false}
                    onChange={e => set('reportar', e.target.checked)} />
                  <span className="text-xs">Se reporta %CV</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <input type="checkbox" disabled={isReadOnly}
                    checked={!!formData.tiene_metodo_muestreo}
                    onChange={e => set('tiene_metodo_muestreo', e.target.checked)} />
                  <span className="text-xs">Método de toma de muestreo</span>
                </div>
                {formData.tiene_metodo_muestreo && (
                  <input type="text" disabled={isReadOnly}
                    placeholder="Describir método..."
                    value={formData.metodo_muestreo}
                    onChange={e => set('metodo_muestreo', e.target.value)}
                    className={`mt-1 w-full h-6 text-xs px-1 border border-gray-300 rounded ${isReadOnly ? 'bg-transparent border-0 shadow-none cursor-not-allowed' : ''}`}
                  />
                )}
              </td>
              <td className={TD} />
              <td className={`${TD} whitespace-nowrap`}>
                <div className="font-medium">Núm. de hilos(tira):</div>
                <Input type="number" className={inputCls('w-full')}
                  value={formData.num_hilos_tira} readOnly={isReadOnly}
                  onChange={e => set('num_hilos_tira', e.target.value)} />
              </td>
            </tr>

          </tbody>
        </table>

        {/* Selector de tablas activas (solo en edición/crear) */}
        {!isReadOnly && (
          <div className="border rounded p-2 bg-muted/40">
            <p className="text-xs font-medium mb-1">Tablas a incluir (según solicitado por el cliente):</p>
            <div className="flex gap-6 flex-wrap">
              {ALL_TABLES.map(t => (
                <label key={t} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox"
                    checked={formData.tablas_activas.includes(t)}
                    onChange={() => toggleTabla(t)} />
                  {TABLE_LABELS[t].label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* ── 4 DATA TABLES in 2×2 grid — solo las activas ── */}
        <div className="grid grid-cols-2 gap-4">
          {ALL_TABLES.filter(t => formData.tablas_activas.includes(t)).map(t => {
            const { label, unit } = TABLE_LABELS[t]
            return (
              <div key={t}>
                <table className="w-full text-xs border-collapse border border-gray-500">
                  <thead>
                    <tr>
                      <th colSpan={3} className="border border-gray-500 p-1 text-center font-bold bg-white">
                        {label}
                      </th>
                    </tr>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-500 p-1 text-center w-14">No.</th>
                      <th className="border border-gray-500 p-1 text-center">Urdimbre</th>
                      <th className="border border-gray-500 p-1 text-center">Trama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1,2,3,4,5].map(i => (
                      <tr key={i}>
                        <td className="border border-gray-500 p-1 text-center">{i}</td>
                        <td className="border border-gray-500 p-0.5">
                          <Input className="h-5 text-xs px-1 border-0 shadow-none"
                            value={(formData as any)[`${t}_u${i}`] || ''}
                            readOnly={isReadOnly}
                            onChange={e => set(`${t}_u${i}` as keyof FormData059_1, e.target.value)} />
                        </td>
                        <td className="border border-gray-500 p-0.5">
                          <Input className="h-5 text-xs px-1 border-0 shadow-none"
                            value={(formData as any)[`${t}_t${i}`] || ''}
                            readOnly={isReadOnly}
                            onChange={e => set(`${t}_t${i}` as keyof FormData059_1, e.target.value)} />
                        </td>
                      </tr>
                    ))}
                    <tr className="font-semibold bg-gray-50">
                      <td className="border border-gray-500 p-1">Promedio</td>
                      <td className="border border-gray-500 p-1 text-center">{(formData as any)[`${t}_prom_u`]}</td>
                      <td className="border border-gray-500 p-1 text-center">{(formData as any)[`${t}_prom_t`]}</td>
                    </tr>
                    <tr className="font-semibold bg-gray-50">
                      <td className="border border-gray-500 p-1">Redondeo</td>
                      <td className="border border-gray-500 p-1 text-center">
                        {(formData as any)[`${t}_red_u`] ? `${(formData as any)[`${t}_red_u`]} ${unit}` : unit}
                      </td>
                      <td className="border border-gray-500 p-1 text-center">
                        {(formData as any)[`${t}_red_t`] ? `${(formData as any)[`${t}_red_t`]} ${unit}` : unit}
                      </td>
                    </tr>
                    {(() => {
                      const uVals = [1,2,3,4,5].map(i => (formData as any)[`${t}_u${i}`] || '')
                      const tVals = [1,2,3,4,5].map(i => (formData as any)[`${t}_t${i}`] || '')
                      const dU = calcDesv1(uVals); const dT = calcDesv1(tVals)
                      const pU = (formData as any)[`${t}_prom_u`]; const pT = (formData as any)[`${t}_prom_t`]
                      return (<>
                        <tr className="bg-gray-50 font-semibold">
                          <td className="border border-gray-500 p-1">σ</td>
                          <td className="border border-gray-500 p-1 text-center">{dU}</td>
                          <td className="border border-gray-500 p-1 text-center">{dT}</td>
                        </tr>
                        {formData.reportar !== false && (<>
                          <tr className="bg-gray-50 font-semibold">
                            <td className="border border-gray-500 p-1">CV (%)</td>
                            <td className="border border-gray-500 p-1 text-center">{calcCV1(pU, dU)}</td>
                            <td className="border border-gray-500 p-1 text-center">{calcCV1(pT, dT)}</td>
                          </tr>
                          <tr className="bg-gray-50 font-semibold">
                            <td className="border border-gray-500 p-1 whitespace-nowrap">LC 95%</td>
                            <td className="border border-gray-500 p-1 text-center">{calcLC95_1(dU, uVals)}</td>
                            <td className="border border-gray-500 p-1 text-center">{calcLC95_1(dT, tVals)}</td>
                          </tr>
                        </>)}
                      </>)
                    })()}
                  </tbody>
                </table>
              </div>
            )
          })}
        </div>

        {/* ── FOOTER TABLE ── */}
        <table className="w-full text-xs border-collapse border border-gray-500">
          <tbody>
            <tr>
              <td className="border border-gray-500 px-2 py-1 w-1/2">
                <span className="font-medium">Realizó:</span>
                <Input value={formData.realizo} readOnly
                  className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed mt-0.5" />
              </td>
              <td className="border border-gray-500 px-2 py-1 w-1/2">
                <span className="font-medium">Supervisó:</span>
                <Input value={formData.superviso} readOnly
                  className="h-6 text-xs border-0 shadow-none bg-transparent cursor-not-allowed mt-0.5" />
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="border border-gray-500 px-2 py-1">
                <div className="font-medium mb-1 flex items-center gap-2">
                  Observaciones:
                  <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    Mostrar en Reporte Cliente
                    <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                      onCheckedChange={(checked) => set('mostrarObservaciones', checked)} />
                  </span>
                </div>
                <Textarea value={formData.observaciones} readOnly={isReadOnly} rows={2}
                  className={`text-xs ${isReadOnly ? 'border-0 shadow-none bg-transparent resize-none' : ''}`}
                  onChange={e => set('observaciones', e.target.value)} />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Desviación al método */}
        <div>
          <label className="text-xs font-medium block mb-1">Desviación al método</label>
          <Textarea value={formData.desv_metodo} readOnly={isReadOnly} rows={2}
            className={`text-xs ${isReadOnly ? 'bg-muted-foreground/10 cursor-not-allowed' : ''}`}
            onChange={e => set('desv_metodo', e.target.value)} />
        </div>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="outline" onClick={handleBackFlush}>Cancelar</Button>}
          {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
            <>
              <Button variant="default" onClick={handleAccion}>Aceptar</Button>
              <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
              <Button variant="secondary" onClick={() => setEditMode(true)}>Editar</Button>
            </>
          )}
          {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
            <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
          )}
          {mode === 'create' && (
            <Button onClick={handleSave} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar Formulario'}</Button>
          )}
        </div>

      </CardContent>

      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={async (razon: string) => {
          setRechazoLoading(true)
          try {
            // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
            // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
            if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
            while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
            await actualizarAceptado(rowId, 'rechazado', razon, undefined, nomId || undefined)
            toast.success('Prueba rechazada')
            setShowRechazoModal(false)
            onRefreshBackend?.()
          } catch (e: any) { toast.error(e.message) }
          finally { setRechazoLoading(false) }
        }}
      />
    </Card>
    </>
  )
}
